import { useGetAvailableRequests, useRespondToRequest } from "@/api/product-request";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

interface VendorProductRequestDialogProps {
	trigger?: React.ReactNode;
}

export function VendorProductRequestDialog({ trigger }: VendorProductRequestDialogProps) {
	const { data: requests, isPending } = useGetAvailableRequests();
	const respondToRequest = useRespondToRequest();

	const handleAccept = async (requestId: string) => {
		await respondToRequest.mutateAsync({
			requestId,
			accept: true
		});
	};

	const handleReject = async (requestId: string) => {
		await respondToRequest.mutateAsync({
			requestId,
			accept: false
		});
	};

	const requestCount = requests?.data?.data?.length ?? 0;

	return (
		<Dialog>
			<DialogTrigger asChild>
				{trigger || (
					<Button variant="outline" className="gap-2">
						<span>Product Requests</span>
						{requestCount > 0 && (
							<span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-white">
								{requestCount}
							</span>
						)}
					</Button>
				)}
			</DialogTrigger>
			<DialogContent className="max-w-4xl h-[90vh]">
				<DialogHeader>
					<DialogTitle>Product Requests</DialogTitle>
					<DialogDescription>
						View all product requests and respond to them.
					</DialogDescription>
				</DialogHeader>
				<div className="overflow-y-auto">
					{isPending
						? (
								<div className="flex items-center justify-center py-8">
									Loading requests...
								</div>
							)
						: requestCount > 0
							? (
									<div className="space-y-4">
										{requests?.data.data.map(request => (
											<Card key={request.id} className="shadow-sm border-l-4 border-l-primary">
												<CardContent className="pt-6">
													<div className="flex flex-col gap-4">
														<div className="flex items-start justify-between">
															<div className="flex-1">
																<h3 className="font-semibold text-lg">
																	{request.batch.buyerRequest.productDescription}
																</h3>
																<p className="text-sm text-muted-foreground">
																	Qty:
																	{" "}
																	{request.batch.buyerRequest.quantity}
																</p>
																<p className="text-sm text-muted-foreground">
																	{new Date(request.batch.buyerRequest.createdAt).toLocaleString()}
																</p>
																<div className="mt-2">
																	<span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
																		{request.status}
																	</span>
																</div>
															</div>
															<Sheet>
																<SheetTrigger asChild>
																	<Button variant="link" className="text-sm">
																		More
																	</Button>
																</SheetTrigger>
																<SheetContent className="w-[400px] sm:w-[540px]">
																	<SheetHeader>
																		<SheetTitle>Request Details</SheetTitle>
																		<SheetDescription>
																			View complete information about this request
																		</SheetDescription>
																	</SheetHeader>
																	<ScrollArea className="h-[calc(100vh-200px)] mt-6">
																		<div className="space-y-6">
																			<div>
																				<h4 className="text-sm font-medium mb-2">Product Description</h4>
																				<p className="text-sm text-muted-foreground">
																					{request.batch.buyerRequest.productDescription}
																				</p>
																			</div>
																			<div>
																				<h4 className="text-sm font-medium mb-2">Quantity</h4>
																				<p className="text-sm text-muted-foreground">
																					{request.batch.buyerRequest.quantity}
																				</p>
																			</div>
																			<div>
																				<h4 className="text-sm font-medium mb-2">Buyer Information</h4>
																				<div className="space-y-1">
																					<p className="text-sm text-muted-foreground">
																						Name:
																						{" "}
																						{request.batch.buyerRequest.buyer.fullName}
																					</p>
																					<p className="text-sm text-muted-foreground">
																						Email:
																						{" "}
																						{request.batch.buyerRequest.buyer.email}
																					</p>
																					{request.batch.buyerRequest.buyer.phoneNumber && (
																						<p className="text-sm text-muted-foreground">
																							Phone:
																							{" "}
																							{request.batch.buyerRequest.buyer.phoneNumber}
																						</p>
																					)}
																				</div>
																			</div>
																			<div>
																				<h4 className="text-sm font-medium mb-2">Request Details</h4>
																				<div className="space-y-1">
																					<p className="text-sm text-muted-foreground">
																						Status:
																						{" "}
																						{request.status.toLowerCase()}
																					</p>
																					<p className="text-sm text-muted-foreground">
																						Created:
																						{" "}
																						{new Date(request.batch.buyerRequest.createdAt).toLocaleString()}
																					</p>
																					<p className="text-sm text-muted-foreground">
																						Expires:
																						{" "}
																						{new Date(request.batch.expiresAt).toLocaleString()}
																					</p>
																				</div>
																			</div>
																			{request.batch.buyerRequest.preferredVendorState && (
																				<div>
																					<h4 className="text-sm font-medium mb-2">Preferred Location</h4>
																					<p className="text-sm text-muted-foreground">
																						{request.batch.buyerRequest.preferredVendorState}
																					</p>
																				</div>
																			)}
																		</div>
																	</ScrollArea>
																	<div className="flex gap-3 mt-6">
																		<Button
																			variant="destructive"
																			className="flex-1"
																			onClick={() => handleReject(request.id)}
																			disabled={respondToRequest.isPending}
																		>
																			Reject
																		</Button>
																		<Button
																			className="flex-1"
																			onClick={() => handleAccept(request.id)}
																			disabled={respondToRequest.isPending}
																		>
																			Accept
																		</Button>
																	</div>
																</SheetContent>
															</Sheet>
														</div>

														<div className="flex gap-3">
															<Button
																variant="destructive"
																className="flex-1"
																onClick={() => handleReject(request.id)}
																disabled={respondToRequest.isPending}
															>
																Reject
															</Button>
															<Button
																className="flex-1"
																onClick={() => handleAccept(request.id)}
																disabled={respondToRequest.isPending}
															>
																Accept
															</Button>
														</div>
													</div>
												</CardContent>
											</Card>
										))}
									</div>
								)
							: (
									<div className="flex items-center justify-center py-8 text-muted-foreground">
										No product requests found
									</div>
								)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
